import { CSSProperties, KeyboardEvent } from 'react'

import { ImagePreviewModal } from '@/components/ui/image-preview-modal'
import { popupFactory } from '@/factories'

export const isEmptyImage = (src?: string | null) => !src || !src.trim()

export interface ImagePreviewTriggerOptions {
  src?: string | null
  title: string
  alt?: string
  images?: string[]
  initialIndex?: number
  disabled?: boolean
}

export interface ImagePreviewTriggerProps {
  onClick?: VoidFunction
  onKeyDown?: (event: KeyboardEvent) => void
  role?: 'button'
  style?: CSSProperties
  tabIndex?: number
}

/**
 * Props that turn any element (an `<img>`, an avatar wrapper) into a zoom trigger that opens
 * `ImagePreviewModal`. Returns `{}` when there is nothing real to zoom (no src or the default
 * src), so it is safe to spread unconditionally.
 */
export const getImagePreviewTriggerProps = ({
  src,
  title,
  alt,
  images,
  initialIndex,
  disabled,
}: ImagePreviewTriggerOptions): ImagePreviewTriggerProps => {
  const gallery = images?.filter(Boolean) ?? []
  const first = gallery[initialIndex ?? 0] ?? src

  if (disabled || isEmptyImage(first)) return {}

  const open = () =>
    popupFactory.open(
      ImagePreviewModal,
      { alt, images: gallery.length > 0 ? gallery : [src as string], initialIndex, title },
      { suspendCurrent: true },
    )

  return {
    onClick: open,
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        open()
      }
    },
    role: 'button' as const,
    style: { cursor: 'zoom-in' },
    tabIndex: 0,
  }
}
